import React, { useState } from 'react';
import { CheckCircle2, AlertCircle, Pause, Loader2, Send, Settings, Pencil, Check, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { whatsappApi, WhatsAppChannel } from '../../../services/whatsappApi';

// Super consultant check that survives impersonation. While impersonating
// a client, `user_role` flips to 'client' but the real role is preserved
// at `original_user_role` — both are checked so the admin affordance stays
// visible even when viewing the marketplace as the impersonated client.
const isSuperConsultant = () => {
  if (localStorage.getItem('user_role') === 'super') return true;
  const isImpersonating = localStorage.getItem('impersonation_token') !== null;
  return isImpersonating && localStorage.getItem('original_user_role') === 'super';
};

interface WhatsAppChannelStatusProps {
  clientId: string;
  channel: WhatsAppChannel;
  onChange: () => void;
}

const STATUS_STYLES: Record<WhatsAppChannel['status'], { bg: string; text: string; icon: React.ReactNode; label: string }> = {
  active: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    icon: <CheckCircle2 size={14} />,
    label: 'Active',
  },
  pending: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    icon: <Loader2 size={14} className="animate-spin" />,
    label: 'Pending',
  },
  paused: {
    bg: 'bg-gray-100',
    text: 'text-gray-600',
    icon: <Pause size={14} />,
    label: 'Paused',
  },
  error: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    icon: <AlertCircle size={14} />,
    label: 'Error',
  },
};

export const WhatsAppChannelStatus: React.FC<WhatsAppChannelStatusProps> = ({
  clientId,
  channel,
  onChange,
}) => {
  const [showTest, setShowTest] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testing, setTesting] = useState(false);
  const [testMode, setTestMode] = useState<'free' | 'template'>('free');
  const [testBody, setTestBody] = useState('This is a test message from your WhatsApp agent.');
  const [tplName, setTplName] = useState('');
  const [tplLang, setTplLang] = useState('en_US');
  const [tplVars, setTplVars] = useState('');     // newline-separated "name=value"

  const [editingMeta, setEditingMeta] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [draftPhoneId, setDraftPhoneId] = useState(channel.config?.phone_number_id || '');
  const [draftWabaId, setDraftWabaId] = useState(channel.config?.waba_id || '');

  const phoneNumberId = channel.config?.phone_number_id || '—';
  const wabaId = channel.config?.waba_id || '—';
  const statusStyle = STATUS_STYLES[channel.status];
  const canEditMeta = isSuperConsultant();

  const sendTest = async () => {
    if (!testPhone.trim()) {
      toast.error('Phone number required');
      return;
    }
    if (testMode === 'template' && !tplName.trim()) {
      toast.error('Template name required');
      return;
    }
    setTesting(true);
    try {
      const options =
        testMode === 'template'
          ? {
              template_name: tplName.trim(),
              template_language: tplLang.trim() || 'en_US',
              template_body_params: tplVars
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean),
            }
          : { body: testBody };
      const res = await whatsappApi.testSend(
        clientId,
        channel.id,
        testPhone.trim(),
        options,
      );
      if (res.ok) {
        toast.success(
          testMode === 'template'
            ? 'Template send accepted by Meta — check your phone'
            : 'Test message sent',
        );
      } else {
        toast.error(`Send failed: ${res.error || 'unknown error'}`);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || 'Send failed');
    } finally {
      setTesting(false);
    }
  };

  const saveMeta = async () => {
    const trimmedPhone = draftPhoneId.trim();
    const trimmedWaba = draftWabaId.trim();
    if (!trimmedPhone) {
      toast.error('phone_number_id required');
      return;
    }
    setSavingMeta(true);
    try {
      await whatsappApi.updateChannel(clientId, channel.id, {
        phone_number_id: trimmedPhone,
        waba_id: trimmedWaba || undefined,
      });
      toast.success('Channel updated');
      setEditingMeta(false);
      onChange();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingMeta(false);
    }
  };

  const cancelMetaEdit = () => {
    setDraftPhoneId(channel.config?.phone_number_id || '');
    setDraftWabaId(channel.config?.waba_id || '');
    setEditingMeta(false);
  };

  const togglePause = async () => {
    const next = channel.status === 'active' ? 'paused' : 'active';
    try {
      await whatsappApi.updateChannel(clientId, channel.id, { status: next });
      toast.success(`Channel ${next}`);
      onChange();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to update');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <div className="font-medium text-gray-900">
              {channel.display_name || 'WhatsApp Channel'}
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {channel.access_mode === 'private' ? 'Private — invited recipients only' : 'Public — anyone can chat'}
            </div>
          </div>
        </div>
        <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
          {statusStyle.icon}
          {statusStyle.label}
        </div>
      </div>

      {/* Body — phone_number_id / waba_id are super-consultant only (platform credentials, not client-facing) */}
      <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <Field label="Provider" value="Meta Cloud API" />
        <Field
          label="Last activity"
          value={
            channel.last_inbound_at
              ? new Date(channel.last_inbound_at).toLocaleString()
              : 'No messages yet'
          }
        />
        {canEditMeta && !editingMeta && (
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1 flex items-center gap-1.5">
              Meta IDs
              <button
                onClick={() => setEditingMeta(true)}
                className="text-gray-400 hover:text-gray-700"
                title="Edit phone_number_id / waba_id"
              >
                <Pencil size={11} />
              </button>
            </div>
            <div className="font-sans text-xs text-gray-900 leading-relaxed">
              <div>phone: {phoneNumberId}</div>
              <div>waba: {wabaId}</div>
            </div>
          </div>
        )}
      </div>

      {canEditMeta && editingMeta && (
        <div className="px-5 py-4 border-t border-amber-200 bg-amber-50/40">
          <div className="text-xs font-medium text-amber-800 mb-3">
            Editing platform credentials — visible to super consultant only
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">phone_number_id</label>
              <input
                value={draftPhoneId}
                onChange={(e) => setDraftPhoneId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs font-sans bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
                placeholder="1075102892353959"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">waba_id</label>
              <input
                value={draftWabaId}
                onChange={(e) => setDraftWabaId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-xs font-sans bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
                placeholder="977311541413330"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={saveMeta}
              disabled={savingMeta}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap"
            >
              {savingMeta ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              {savingMeta ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={cancelMetaEdit}
              disabled={savingMeta}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 whitespace-nowrap"
            >
              <X size={12} />
              Cancel
            </button>
          </div>
        </div>
      )}

      {channel.error_message && (
        <div className="px-5 py-3 border-t border-red-100 bg-red-50">
          <div className="text-xs text-red-700">
            <span className="font-medium">Last error:</span> {channel.error_message}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex flex-wrap gap-2">
        <button
          onClick={() => setShowTest((v) => !v)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 whitespace-nowrap"
        >
          <Send size={13} />
          Send test
        </button>
        <button
          onClick={togglePause}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 whitespace-nowrap"
        >
          <Settings size={13} />
          {channel.status === 'active' ? 'Pause' : 'Resume'}
        </button>
      </div>

      {showTest && (
        <div className="px-5 py-4 border-t border-gray-200 bg-gray-50 space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Recipient phone (E.164)
            </label>
            <input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="+18761234567"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
            />
          </div>

          <div className="flex gap-1 text-xs">
            <button
              type="button"
              onClick={() => setTestMode('free')}
              className={`px-3 py-1.5 rounded-lg border ${testMode === 'free' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'}`}
            >
              Free-form text
            </button>
            <button
              type="button"
              onClick={() => setTestMode('template')}
              className={`px-3 py-1.5 rounded-lg border ${testMode === 'template' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-100'}`}
            >
              Approved template
            </button>
          </div>

          {testMode === 'free' ? (
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Message body</label>
              <textarea
                value={testBody}
                onChange={(e) => setTestBody(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                Free-form only delivers within the 24h customer-service window. Outside that, Meta returns <code className="bg-white px-1 rounded">131047</code>.
              </p>
            </div>
          ) : (
            <div className="space-y-2 border border-emerald-200 bg-emerald-50/40 rounded-lg p-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Template name</label>
                  <input
                    value={tplName}
                    onChange={(e) => setTplName(e.target.value)}
                    placeholder="navcro_daily_checkin"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Language</label>
                  <input
                    value={tplLang}
                    onChange={(e) => setTplLang(e.target.value)}
                    placeholder="en_US"
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Body variables (one per line — <code className="bg-white px-1 rounded">name=value</code>)
                </label>
                <textarea
                  value={tplVars}
                  onChange={(e) => setTplVars(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm font-sans bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200"
                  placeholder={'first_name=Maria'}
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Names match the <code className="bg-white px-1 rounded">{'{{name}}'}</code> placeholders in your approved Meta template body. Leave blank if your template has no variables.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={sendTest}
              disabled={testing}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap"
            >
              {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {testing ? 'Sending…' : 'Send'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Field: React.FC<{ label: string; value: React.ReactNode; mono?: boolean }> = ({
  label,
  value,
  mono,
}) => (
  <div>
    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">
      {label}
    </div>
    <div className={`text-gray-900 ${mono ? 'font-sans text-xs' : ''}`}>{value}</div>
  </div>
);

export default WhatsAppChannelStatus;
